/* ═══════════════════════════════════════════════════════════════════════════
   Client mirror of server/permissions.js — capability-based access control.

   The server is the source of truth and enforces every guarded endpoint. This
   module exists so the client can hide nav/routes the user can't use, and so a
   user object that predates the permissions feature (stale localStorage, no
   `permissions` map) still gates correctly by falling back to the role matrix.

   Prefer `can(user, cap)`:
     1. if user.permissions[cap] is set (fresh from /me or login), use it;
     2. otherwise derive from the role matrix + any permission_overrides.
   ═══════════════════════════════════════════════════════════════════════════ */

export const CAPABILITIES = [
  { key: 'users.manage',             label: 'Team Management',          group: 'Administration', description: 'Create, edit, and deactivate users; set roles and reporting lines.' },
  { key: 'settings.manage',          label: 'Settings',                 group: 'Administration', description: 'App settings and notification-list configuration.' },
  { key: 'jobs.archive',             label: 'Archive & Restore Jobs',   group: 'Administration', description: "Archive jobs, and restore them from the Dashboard's Archived tab. Nothing is ever deleted." },
  { key: 'audit.view',               label: 'Audit Log',                group: 'Oversight',      description: 'View the system-wide audit trail.' },
  { key: 'schedule_log.view',        label: 'Schedule Change Log',      group: 'Oversight',      description: 'View the cross-project schedule change history.' },
  { key: 'preconstruction.view',     label: 'Pre-Construction',         group: 'Operations',     description: 'View the pre-construction pipeline.' },
  { key: 'workload.view',            label: 'Workload',                 group: 'Operations',     description: 'View the team workload dashboard.' },
  { key: 'closeout.manage',          label: 'Manage Closeout Documents', group: 'Operations',    description: 'Upload, organize, rename, and delete a project\'s closeout documents (kept in Facility Navigator).' },
  { key: 'lighting.view_all',        label: 'All Lighting Jobs',        group: 'Operations',     description: 'See every job with a lighting budget in CE (or an L job number), not only the ones assigned to them.' },
  { key: 'jobs.view_all',            label: 'Every Job',                group: 'Operations',     description: 'See every job, not only the ones assigned to them (Admins and VPs always do).' },
  { key: 'subs.manage',              label: 'Subcontractor Mgmt',       group: 'Operations',     description: "Subcontractors' standing with E3: MSAs, insurance and other documents, job history and notes." },
  { key: 'subs.restore',             label: 'Restore Sub Documents',    group: 'Operations',     description: "Restore a sub's removed files and folders from a Documents task's recycle bin (kept 365 days on E3 Subs)." },
  { key: 'subs.applications',        label: 'Sub Applications',         group: 'Operations',     description: 'See and decide the applications from subcontractors who want to work with E3 (workwith.e3es.com).' },
  { key: 'ar.view',                  label: 'AR / Collections',         group: 'Financials',     description: 'View accounts receivable and collections.' },
  { key: 'billings_projection.view', label: 'Billings Projection',      group: 'Financials',     description: 'View the all-projects billings projection.' },
  { key: 'jobcosts.view',            label: 'Job Costs (all projects)', group: 'Financials',     description: 'View the cross-project job-cost dashboard.' },
  { key: 'jobcosts.contracts',       label: 'Set P/E/M Contracts',      group: 'Financials',     description: 'Set the contract value of a P, E or M job (no project page), inside the job on Job Costs.' },
];

export const CAPABILITY_KEYS = CAPABILITIES.map((c) => c.key);

// VP shares Admin's rank — one top tier (same access, same org level).
export const ROLE_RANK = { admin: 5, vp: 5, construction_director: 3, construction_manager: 2, pm: 1, e3_direct_director: 3, e3_direct_pm: 1, sales_manager: 1, sales_person: 1, project_developer: 1, accounting: 1 };
const VIEW_CAPS = ['audit.view', 'schedule_log.view', 'preconstruction.view', 'workload.view', 'ar.view', 'billings_projection.view', 'jobcosts.view'];
const CLOSEOUT_ROLES = ['admin', 'vp', 'construction_director', 'construction_manager', 'pm', 'e3_direct_director', 'e3_direct_pm'];
const LIGHTING_ROLES = ['e3_direct_director', 'e3_direct_pm']; // the lighting team (PSR #61)
// Accounting (2026-10-20): every job, AR + Billings Projection + Job Costs, archive / restore, P/E/M contracts,
// Subcontractor Mgmt.
const ACCOUNTING_CAPS = ['jobs.view_all', 'ar.view', 'billings_projection.view', 'jobcosts.view', 'jobs.archive', 'jobcosts.contracts', 'subs.manage', 'subs.restore'];
// Sales (2026-10-30): AR / Collections, and Job Costs, whose permission also opens BoMoCo.
const SALES_ROLES = ['sales_manager', 'sales_person'];
const SALES_CAPS = ['ar.view', 'jobcosts.view'];

// Role → default capability map. Mirrors server/permissions.js exactly.
export function defaultsForRole(roleKey) {
  const rank = ROLE_RANK[roleKey] || 0;
  const granted = {};
  for (const k of CAPABILITY_KEYS) granted[k] = false;
  if (rank >= ROLE_RANK.construction_manager) for (const k of VIEW_CAPS) granted[k] = true;
  if (rank >= ROLE_RANK.vp) { granted['users.manage'] = true; granted['jobs.view_all'] = true; }
  if (rank >= ROLE_RANK.admin) granted['settings.manage'] = true;
  if (roleKey === 'admin') { granted['jobs.archive'] = true; granted['jobcosts.contracts'] = true; } // the Admin role only, not VPs
  if (rank >= ROLE_RANK.vp) granted['subs.manage'] = true; // Subcontractor Mgmt: Admins, VPs + Accounting (2026-10-20)
  if (rank >= ROLE_RANK.vp) granted['subs.restore'] = true; // restoring a sub's removed documents: Admins, VPs + Accounting (2026-10-25)
  // Sub applications: Admins, the VP and the construction directors (Duncan 2026-10-28: "set the sub applications as
  // Grant by default for the vice president of construction and construction directors"); anyone else by a grant.
  if (['admin', 'vp', 'construction_director'].includes(roleKey)) granted['subs.applications'] = true;
  if (roleKey === 'accounting') for (const k of ACCOUNTING_CAPS) granted[k] = true;
  if (SALES_ROLES.includes(roleKey)) for (const k of SALES_CAPS) granted[k] = true; // Sales: AR + Job Costs (and BoMoCo)
  if (CLOSEOUT_ROLES.includes(roleKey)) granted['closeout.manage'] = true; // construction roles; sales + developers view only
  if (LIGHTING_ROLES.includes(roleKey)) granted['lighting.view_all'] = true;
  return granted;
}

export const ROLE_DEFAULTS = Object.keys(ROLE_RANK).reduce((acc, r) => {
  acc[r] = defaultsForRole(r);
  return acc;
}, {});

function cleanOverrides(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const clean = {};
  for (const [k, v] of Object.entries(raw)) {
    if (CAPABILITY_KEYS.includes(k) && typeof v === 'boolean') clean[k] = v;
  }
  return clean;
}

// Effective permission map for a user, with the same precedence as the server.
export function effectivePermissions(user) {
  if (!user) return {};
  const base = defaultsForRole(user.role);
  const overrides = cleanOverrides(user.permission_overrides);
  return { ...base, ...overrides };
}

// The one helper the rest of the app should use.
export function can(user, capability) {
  if (!user) return false;
  if (user.permissions && typeof user.permissions[capability] === 'boolean') {
    return user.permissions[capability];
  }
  return effectivePermissions(user)[capability] === true;
}
